import Loader from '@/components/Loader';
import axios from 'axios';
import { Lottie, type LottieProps } from 'lottie-react';
import { FC, useEffect, useState } from 'react';

interface Props extends Omit<LottieProps, 'src'> {
  animationUrl?: string;
}

// Simple in-memory cache to avoid re-fetching the same animation JSON
const animationCache = new Map<string, unknown>();

const LottieFromUrl: FC<Props> = ({ animationUrl, ...props }) => {
  const [animationData, setAnimationData] = useState<unknown>(null);

  useEffect(() => {
    if (!animationUrl) {
      return;
    }

    // Return cached data immediately if available
    const cached = animationCache.get(animationUrl);
    if (cached) {
      setAnimationData(cached);
      return;
    }

    let cancelled = false;
    const fetchAnimation = async () => {
      try {
        const response = await axios.get(animationUrl);
        if (!cancelled) {
          animationCache.set(animationUrl, response.data);
          setAnimationData(response.data);
        }
      } catch (error) {
        console.error('Error fetching animation:', error);
        if (!cancelled) {
          setAnimationData(undefined);
        }
      }
    };
    void fetchAnimation();

    return () => {
      cancelled = true;
    };
  }, [animationUrl]);

  if (!animationData) {
    return (
      <div className="relative h-[70dvh]">
        <Loader className="absolute inset-0 bottom-20 top-20" />
      </div>
    );
  }

  return <Lottie {...props} src={animationData as object} />;
};

export default LottieFromUrl;
